import { Redirect, useRouter } from 'expo-router'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { CodeInput } from '@/src/elements/code-input/CodeInput'
import { Spacer } from '@/src/elements/spacer/Spacer'
import { Typography } from '@/src/elements/text/Typography'
import { useOtpAuthentication } from '@/src/features/auth/services/useOtpAuthentication'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { TEST_ID } from '@/src/general/constants/testId'
import { useInterval } from '@/src/general/hooks/useInterval'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { useOtpAuthenticationSession } from '../context/OtpAuthenticationContext'

type TOtpCodeScreenProps = {
  redirectTo?: string
}
const RESEND_COOLDOWN_SECONDS = 60
const CODE_LENGTH = 6

export const OtpCodeScreen = ({ redirectTo }: TOtpCodeScreenProps) => {
  const [code, setCode] = useState('')
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS)
  const session = useOtpAuthenticationSession()
  const router = useRouter()
  const { routes } = useRoutes()
  const { t } = useTranslate()
  const intl = useIntl()
  const destination = redirectTo || routes['prayer-space']()
  const { initializationMutation, authenticationMutation, authenticateOtp, errors } = useOtpAuthentication(destination)
  const { email, mode, csrfToken } = session
  const { codeKey: codeErrorKey, generalKey: authenticationGeneralErrorKey } = errors.authentication

  useInterval(
    () => setResendCooldown((remainingSeconds) => Math.max(remainingSeconds - 1, 0)),
    resendCooldown > 0 ? 1000 : null,
  )

  if (!email || !mode || !csrfToken) return <Redirect href={routes.authConnect()} />

  const initializeOtp = async () => {
    const nextCsrfToken = await initializationMutation.mutateAsync({ mode, email })
    session.updateSession({ csrfToken: nextCsrfToken })
    setResendCooldown(RESEND_COOLDOWN_SECONDS)
  }

  const authenticate = async () => {
    if (code.length !== CODE_LENGTH) return
    const name = mode === 'signup' ? session.name : undefined

    const result = await authenticateOtp({
      mode,
      csrf_token: csrfToken,
      email,
      code,
      name,
    })
    if (result.status === 'name-error') {
      session.updateSession({ nameErrorKey: result.nameKey })
      router.back()
    } else if (result.status === 'csrf-refreshed') {
      setCode('')
      session.updateSession({ csrfToken: result.csrfToken })
    }
  }

  const handleCodeChange = (value: string) => {
    setCode(value)
    authenticationMutation.reset()
  }

  return (
    <View className="w-full max-h-screen flex-1">
      <View className="mx-auto w-full max-w-lg justify-between flex-1">
        <Spacer y={32} />
        <View className="gap-8">
          <Typography bold size="2xl" center>
            <FormattedMessage id="auth.otp.code.title" />
          </Typography>
          <Typography center>
            <FormattedMessage id="auth.otp.code.description" values={{ email }} />
          </Typography>
          <View className="items-center">
            <CodeInput
              value={code}
              onChangeText={handleCodeChange}
              onSubmit={() => void authenticate().catch(() => {})}
              isError={!!codeErrorKey}
              errorMessage={codeErrorKey ? t(codeErrorKey) : undefined}
            />
          </View>
          {authenticationGeneralErrorKey && <ErrorMessage defaultErrorKey={authenticationGeneralErrorKey} />}
          {errors.initialization.emailKey && <ErrorMessage defaultErrorKey={errors.initialization.emailKey} />}
          {errors.initialization.generalKey && <ErrorMessage defaultErrorKey={errors.initialization.generalKey} />}
        </View>
      </View>
      <View className="mx-6 gap-2">
        <Button
          colors="primary"
          variant="light"
          fullWidth
          isLoading={initializationMutation.isPending}
          isDisabled={initializationMutation.isPending || resendCooldown > 0}
          onPress={() => void initializeOtp().catch(() => {})}
          testID={TEST_ID.ME.AUTHENTICATION.OTP_RESEND_BUTTON}
        >
          <FormattedMessage id="auth.otp.code.resend" />
          {resendCooldown > 0 &&
            ` (${intl.formatNumber(resendCooldown, { style: 'unit', unit: 'second', unitDisplay: 'narrow' })})`}
        </Button>
        <Button
          colors="primary"
          variant="solid"
          fullWidth
          isLoading={authenticationMutation.isPending}
          isDisabled={authenticationMutation.isPending || code.length !== 6}
          onPress={() => void authenticate().catch(() => {})}
          testID={TEST_ID.ME.AUTHENTICATION.OTP_CODE_SUBMIT}
        >
          <FormattedMessage id="words.i-continue" />
        </Button>
      </View>
    </View>
  )
}
